/**
 * ============================================================
 * 公式配置页面 (Formulas) - v61
 * 支持中文标签表达式 [总流水]*0.01
 * 字段按7大分类分组，默认展开
 * 语法检测（自动） + 选择经纪人+月份测试（月总额）
 * 字段选择器：自适应高度 + 分类/标签可拖拽排序
 * ============================================================
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { formulaApi } from '../api';
import type { Formula } from '../types';
import {
  Calculator, Plus, Pencil, Trash2, X, CheckCircle,
  ChevronRight, ChevronDown, AlertTriangle, Play,
  UserCheck, Code2, Sparkles, Calendar,
  GripVertical
} from 'lucide-react';

// ===== 字段标签 → 数据库字段映射 =====
const FIELD_LABELS: Record<string, string> = {
  '结算月份': 'settlement_month', '主播昵称': 'anchor_name', '主播uid': 'anchor_uid',
  '主播房间号': 'anchor_room_id', '入会时间': 'join_date', '首次开播时间': 'first_stream_date',
  '经纪人昵称': 'broker_name', '经纪人uid': 'broker_uid',
  '招募经纪人昵称': 'recruit_broker_name', '招募经纪人uid': 'recruit_broker_uid',
  '主播支付': 'anchor_payment', '主播总激励': 'anchor_total_incentive',
  '主播非自提礼物收益': 'anchor_non_self_gift_income', '非自提合计底薪': 'non_self_base_salary',
  '合计活动收益': 'total_activity_income', '非自提运营奖励': 'non_self_operation_reward',
  '非自提运营惩罚': 'non_self_operation_penalty', '主播负向治理收益总扣除': 'governance_deduction',
  '主播负向治理收益总补回': 'governance_recovery', '开播时长(含连麦时长)': 'stream_duration',
  '有效开播天': 'valid_stream_days', '有效开播时长': 'valid_stream_hours',
  '是否入会无首播主播': 'is_no_first_stream', '是否TOP主播': 'is_top_anchor',
  'TOPstar主播评级': 'topstar_level', '总流水': 'total_stream',
  '是否是政策新主播': 'is_policy_new_anchor', '新主播任务流水': 'new_anchor_task_stream',
  '新主播激励分成': 'new_anchor_incentive_share', '新主播激励': 'new_anchor_incentive',
  '新主播结算周期': 'new_anchor_settlement_period', '直播有效天/新主播周期': 'valid_days_in_period',
  '直播有效时长/新主播周期': 'valid_hours_in_period', '当月新主播流水': 'current_month_new_anchor_stream',
  '次月待结算新主播流水': 'next_month_pending_new_anchor_stream', '老主播任务流水': 'old_anchor_task_stream',
  '老主播任务分成': 'old_anchor_task_share', '老主播任务激励': 'old_anchor_task_incentive',
  'TOP主播任务分成': 'top_anchor_task_share', 'TOP主播任务激励': 'top_anchor_task_incentive',
  '成长任务分成': 'growth_task_share', '成长任务激励': 'growth_task_incentive',
  '流水任务分成': 'stream_task_share', '流水任务激励': 'stream_task_incentive',
  '优质奖励': 'quality_reward', '未成年扣除': 'minor_deduction',
};

// ===== 默认分类 =====
const FIELD_GROUPS_DEFAULT = [
  { name: '时间类', fields: ['结算月份', '入会时间', '首次开播时间', '新主播结算周期'] },
  { name: '支付类', fields: ['主播支付', '主播总激励', '非自提合计底薪', '合计活动收益', '非自提运营奖励', '非自提运营惩罚', '主播负向治理收益总扣除', '主播负向治理收益总补回'] },
  { name: '流水类', fields: ['总流水', '主播非自提礼物收益', '新主播任务流水', '当月新主播流水', '次月待结算新主播流水', '老主播任务流水'] },
  { name: '分成类', fields: ['新主播激励分成', '新主播激励', '老主播任务分成', '老主播任务激励', 'TOP主播任务分成', 'TOP主播任务激励', '成长任务分成', '成长任务激励', '流水任务分成', '流水任务激励', '优质奖励', '未成年扣除'] },
  { name: '周期类', fields: ['开播时长(含连麦时长)', '有效开播天', '有效开播时长', '直播有效天/新主播周期', '直播有效时长/新主播周期'] },
  { name: '信息类', fields: ['主播昵称', '主播uid', '主播房间号', '是否入会无首播主播', '是否TOP主播', 'TOPstar主播评级', '是否是政策新主播', '主播数量'] },
  { name: '身份类', fields: ['经纪人昵称', '经纪人uid', '招募经纪人昵称', '招募经纪人uid'] },
];

const STORAGE_KEY = 'formula_field_groups';
const EMPTY_FORMULA = { id: 0, name: '', expression: '', description: '', variables: '', status: 'active' as const };

// 反向映射：数据库字段名 → 中文标签
const DB_TO_LABEL: Record<string, string> = {};
for (const [label, dbField] of Object.entries(FIELD_LABELS)) DB_TO_LABEL[dbField] = label;

// ===== 从 localStorage 加载分类（带字段合并）=====
// 特殊字段列表（不在数据库中但可在公式中使用）
const SPECIAL_FIELDS = ['主播数量'];

function loadFieldGroups(): typeof FIELD_GROUPS_DEFAULT {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const groups = JSON.parse(saved) as typeof FIELD_GROUPS_DEFAULT;
      const allSavedFields = new Set(groups.flatMap(g => g.fields));
      const allDefinedFields = new Set([...Object.keys(FIELD_LABELS), ...SPECIAL_FIELDS]);
      // 找出新字段（在定义中但不在保存的分类中）
      const newFields = Array.from(allDefinedFields).filter(f => !allSavedFields.has(f));
      if (newFields.length > 0) {
        // 添加"未分类"分组
        groups.push({ name: '未分类', fields: newFields });
      }
      return groups;
    }
  } catch { /* ignore */ }
  return FIELD_GROUPS_DEFAULT.map(g => ({ ...g }));
}

// ===== 保存分类到 localStorage =====
function saveFieldGroups(groups: typeof FIELD_GROUPS_DEFAULT) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
  } catch { /* ignore */ }
}

export default function Formulas() {
  // ===== 核心状态 =====
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editingFormula, setEditingFormula] = useState<Formula | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_FORMULA });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(FIELD_GROUPS_DEFAULT.map(g => g.name)));
  const [testResult, setTestResult] = useState<number | null>(null);
  const [testError, setTestError] = useState('');
  const [testFieldValues, setTestFieldValues] = useState<Record<string, number>>({});
  const [testMeta, setTestMeta] = useState<{ month?: string; count?: number } | null>(null);
  const [brokers, setBrokers] = useState<{ name: string }[]>([]);
  const [selectedBroker, setSelectedBroker] = useState('');
  const [brokerMonths, setBrokerMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [validation, setValidation] = useState<{ valid: boolean; fields: string[]; invalidFields: string[]; syntaxError: string } | null>(null);
  const [cursorPos, setCursorPos] = useState(0);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompletePrefix, setAutocompletePrefix] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // ===== 拖拽状态 =====
  const [fieldGroups, setFieldGroups] = useState<typeof FIELD_GROUPS_DEFAULT>(loadFieldGroups);
  const [dragState, setDragState] = useState<{
    type: 'group' | 'field' | null;
    groupIdx: number;
    fieldIdx?: number;
    targetGroupIdx?: number;
    targetFieldIdx?: number;
  }>({ type: null, groupIdx: -1 });

  const syntaxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSuccess = useCallback((m: string) => { setToast({ message: m, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((m: string) => { setToast({ message: m, type: 'error' }); setTimeout(() => setToast(null), 5000); }, []);

  // ===== 数据加载 =====
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [list, brokerList] = await Promise.all([formulaApi.list(), formulaApi.brokerList()]);
      setFormulas(list);
      setBrokers(brokerList);
    } catch { showError('加载失败'); }
    finally { setLoading(false); }
  };

  // ===== 选择经纪人后自动加载月份 =====
  useEffect(() => {
    if (selectedBroker) {
      formulaApi.brokerMonths(selectedBroker).then(months => {
        setBrokerMonths(months);
        if (months.length > 0 && !selectedMonth) setSelectedMonth(months[0]);
      }).catch(() => setBrokerMonths([]));
    } else {
      setBrokerMonths([]);
      setSelectedMonth('');
    }
  }, [selectedBroker]);

  // ===== 表达式变化时自动语法检测（防抖500ms）=====
  useEffect(() => {
    const expr = editForm.expression;
    if (!expr.trim()) { setValidation(null); return; }
    if (syntaxTimerRef.current) clearTimeout(syntaxTimerRef.current);
    syntaxTimerRef.current = setTimeout(() => {
      formulaApi.validate(expr)
        .then(result => setValidation(result))
        .catch(() => setValidation(null));
    }, 500);
    return () => { if (syntaxTimerRef.current) clearTimeout(syntaxTimerRef.current); };
  }, [editForm.expression]);

  // ===== 分类/标签变化时自动保存（防抖300ms）=====
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveFieldGroups(fieldGroups);
    }, 300);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [fieldGroups]);

  // ===== 表达式输入处理 =====
  const handleExpressionChange = (value: string) => {
    setEditForm(prev => ({ ...prev, expression: value }));
    setTestResult(null); setTestError(''); setTestMeta(null);
    const beforeCursor = value.slice(0, cursorPos);
    const match = beforeCursor.match(/\[([^\]]*)$/);
    if (match) { setAutocompletePrefix(match[1]); setShowAutocomplete(true); }
    else { setShowAutocomplete(false); }
  };

  const insertField = (label: string) => {
    const textarea = document.getElementById('formula-expression') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editForm.expression;
    const beforeCursor = text.slice(0, start);
    const match = beforeCursor.match(/\[([^\]]*)$/);
    if (match) {
      const newStart = beforeCursor.lastIndexOf('[');
      const newText = text.substring(0, newStart) + `[${label}]` + text.substring(end);
      setEditForm(prev => ({ ...prev, expression: newText }));
      setTimeout(() => { textarea.focus(); textarea.setSelectionRange(newStart + label.length + 2, newStart + label.length + 2); }, 50);
    } else {
      const newText = text.substring(0, start) + `[${label}]` + text.substring(end);
      setEditForm(prev => ({ ...prev, expression: newText }));
      setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + label.length + 2, start + label.length + 2); }, 50);
    }
    setShowAutocomplete(false);
  };

  // ===== 测试公式 =====
  const handleTest = async () => {
    if (!editForm.expression.trim()) { setTestError('请输入表达式'); return; }
    setTestFieldValues({}); setTestMeta(null);
    try {
      let meta: { month?: string; count?: number } | null = null;
      // 直接调用 test API，传入 brokerName 和 month，让后端遍历每个主播计算
      const result = await formulaApi.test(
        editForm.expression, 
        undefined, 
        selectedBroker || undefined, 
        selectedMonth || undefined
      );
      setTestResult(result.result);
      setTestFieldValues(result.fieldValues || {});
      if (selectedBroker && selectedMonth) {
        meta = {
          month: selectedMonth,
          count: result.count || result.details?.length || 0, // 使用后端返回的 count 字段
        };
      }
      setTestMeta(meta);
      setTestError('');
    } catch (e: any) { setTestError(e.message || '测试失败'); setTestResult(null); setTestFieldValues({}); setTestMeta(null); }
  };

  // ===== 保存公式 =====
  const handleSave = async () => {
    if (!editForm.name.trim()) { showError('公式名称必填'); return; }
    if (!editForm.expression.trim()) { showError('表达式必填'); return; }
    try {
      if (!validation?.valid && (validation?.invalidFields.length ?? 0) > 0) {
        showError(`无效字段: ${validation?.invalidFields.join(', ')}`); return;
      }
      if (editingFormula) {
        await formulaApi.update(editingFormula.id, {
          name: editForm.name, expression: editForm.expression,
          variables: editForm.variables, description: editForm.description,
        });
        showSuccess('更新成功');
      } else {
        await formulaApi.create({
          name: editForm.name, expression: editForm.expression,
          variables: editForm.variables, description: editForm.description,
        });
        showSuccess('创建成功');
      }
      setShowEdit(false); setEditingFormula(null); setEditForm({ ...EMPTY_FORMULA });
      await loadData();
    } catch (err: any) { showError(err.message || '保存失败'); }
  };

  // ===== 删除公式 =====
  const handleDelete = async (id: number) => {
    if (!confirm('确定删除？')) return;
    try { await formulaApi.delete(id); showSuccess('删除成功'); await loadData(); } catch { showError('删除失败'); }
  };

  // ===== 打开编辑弹窗 =====
  const openEdit = (f: Formula | null) => {
    setEditingFormula(f);
    setEditForm(f ? { id: f.id, name: f.name || '', expression: f.expression || '', description: (f as any).description || (f as any).notes || '', variables: (f as any).variables || '', status: f.status as any } : { ...EMPTY_FORMULA });
    setTestResult(null); setTestError(''); setTestFieldValues({}); setTestMeta(null);
    setSelectedBroker(''); setSelectedMonth(''); setBrokerMonths([]);
    setShowEdit(true);
  };

  const toggleGroup = (name: string) => {
    const next = new Set(expandedGroups);
    if (next.has(name)) next.delete(name); else next.add(name);
    setExpandedGroups(next);
  };

  const autocompleteItems = showAutocomplete
    ? Object.keys(FIELD_LABELS).filter(l => l.includes(autocompletePrefix)).slice(0, 8)
    : [];

  // ===== 拖拽处理器 =====

  // 开始拖拽分类
  const handleGroupDragStart = (groupIdx: number, e: React.DragEvent) => {
    setDragState({ type: 'group', groupIdx });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `group:${groupIdx}`);
  };

  // 开始拖拽字段
  const handleFieldDragStart = (groupIdx: number, fieldIdx: number, e: React.DragEvent) => {
    setDragState({ type: 'field', groupIdx, fieldIdx });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `field:${groupIdx}:${fieldIdx}`);
  };

  // 拖拽经过分类（用于分类排序）
  const handleGroupDragOver = (targetIdx: number, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragState.type === 'group' && dragState.groupIdx !== targetIdx) {
      setDragState(prev => ({ ...prev, targetGroupIdx: targetIdx }));
    }
  };

  // 拖拽经过字段（用于字段排序和跨分类移动）
  const handleFieldDragOver = (groupIdx: number, fieldIdx: number, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragState.type === 'field') {
      setDragState(prev => ({ ...prev, targetGroupIdx: groupIdx, targetFieldIdx: fieldIdx }));
    }
  };

  // 拖拽离开
  const handleDragLeave = () => {
    setDragState(prev => ({ ...prev, targetGroupIdx: undefined, targetFieldIdx: undefined }));
  };

  // 放置分类（排序）
  const handleGroupDrop = (targetIdx: number, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragState.type === 'group' && dragState.groupIdx !== -1 && dragState.groupIdx !== targetIdx) {
      setFieldGroups(prev => {
        const next = prev.map(g => ({ ...g, fields: [...g.fields] }));
        const [removed] = next.splice(dragState.groupIdx, 1);
        next.splice(targetIdx, 0, removed);
        return next;
      });
    }
    setDragState({ type: null, groupIdx: -1 });
  };

  // 放置字段（排序 / 跨分类移动）
  const handleFieldDrop = (groupIdx: number, fieldIdx: number, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragState.type === 'field' && dragState.groupIdx !== -1 && dragState.fieldIdx !== undefined) {
      const srcGroup = dragState.groupIdx;
      const srcField = dragState.fieldIdx;
      if (srcGroup === groupIdx && srcField === fieldIdx) { setDragState({ type: null, groupIdx: -1 }); return; }

      setFieldGroups(prev => {
        const next = prev.map(g => ({ ...g, fields: [...g.fields] }));
        const [removed] = next[srcGroup].fields.splice(srcField, 1);

        if (srcGroup === groupIdx) {
          // 同类内排序
          const insertAt = fieldIdx > srcField ? fieldIdx : fieldIdx + 1;
          next[groupIdx].fields.splice(insertAt - 1, 0, removed);
        } else {
          // 跨分类移动
          next[groupIdx].fields.splice(fieldIdx, 0, removed);
        }

        // 清理空分类（除了"未分类"）
        return next.filter(g => g.name === '未分类' || g.fields.length > 0);
      });
    }
    setDragState({ type: null, groupIdx: -1 });
  };

  // 拖拽结束
  const handleDragEnd = () => {
    setDragState({ type: null, groupIdx: -1 });
  };

  // ===== 渲染 =====
  return (
    <div className="space-y-6">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Calculator className="w-6 h-6 text-[#F59E0B]" />公式配置</h1>
        <button onClick={() => openEdit(null)} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm"><Plus className="w-4 h-4" />新建公式</button>
      </div>

      {/* 公式列表 */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
      ) : formulas.length === 0 ? (
        <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-12 text-center text-[#94A3B8]">暂无公式</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formulas.map(f => (
            <div key={f.id} className="bg-[#1E293B] border border-[#334155] rounded-xl p-5 hover:border-[#3B82F6]/50 transition-all group">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-sm">{f.name}</h3>
                  <p className="text-xs text-[#64748B] font-mono mt-1">{f.expression}</p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(f)} className="p-1.5 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDelete(f.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#334155]/50">
                <span className={`text-xs px-2 py-0.5 rounded ${f.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{f.status === 'active' ? '启用' : '停用'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 编辑弹窗 */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-auto">
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold">{editingFormula ? '编辑公式' : '新建公式'}</h3>
              <button onClick={() => setShowEdit(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              {/* 左侧：表单 */}
              <div className="lg:col-span-2 space-y-4">
                {/* 公式名称 */}
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1.5 block">公式名称 *</label>
                  <input value={editForm.name} onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder="如：新主播提成" />
                </div>

                {/* 表达式输入 */}
                <div className="relative">
                  <label className="text-xs text-[#94A3B8] mb-1.5 block flex items-center gap-1">
                    <Code2 className="w-3 h-3" />表达式 *（支持中文标签，如 [总流水]*0.01）
                  </label>
                  <textarea
                    id="formula-expression"
                    value={editForm.expression}
                    onChange={e => handleExpressionChange(e.target.value)}
                    onKeyUp={e => setCursorPos((e.target as HTMLTextAreaElement).selectionStart)}
                    onClick={e => setCursorPos((e.target as HTMLTextAreaElement).selectionStart)}
                    className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9] font-mono h-28 resize-none"
                    placeholder="如：[总流水]*0.01 + [主播支付]*0.05"
                  />
                  {showAutocomplete && autocompleteItems.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 bg-[#0F172A] border border-[#334155] rounded-lg mt-1 shadow-xl max-h-40 overflow-auto">
                      {autocompleteItems.map(label => (
                        <button key={label} onClick={() => insertField(label)} className="block w-full text-left px-3 py-2 text-xs text-[#94A3B8] hover:text-white hover:bg-[#334155] transition-all">
                          <Sparkles className="w-3 h-3 inline mr-1 text-[#3B82F6]" />[{label}]
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* 语法检测结果 */}
                {validation && (
                  <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${validation.valid ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                    {validation.valid ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                    <div>
                      {validation.valid ? '语法正确' : `语法警告: ${validation.syntaxError || `无效字段: ${validation.invalidFields.join(', ')}`}`}
                      {validation.fields.length > 0 && <div className="mt-1 text-[#64748B]">字段: {validation.fields.map(f => `[${f}]`).join(', ')}</div>}
                    </div>
                  </div>
                )}

                {/* 备注 */}
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1.5 block">备注</label>
                  <textarea value={editForm.description} onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9] h-16 resize-none" placeholder="公式说明..." />
                </div>

                {/* 测试区域 */}
                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#334155]">
                  <div className="flex items-center gap-2 mb-3">
                    <Play className="w-4 h-4 text-[#3B82F6]" />
                    <span className="text-sm font-medium">测试公式</span>
                  </div>
                  
                  {/* 示例说明 */}
                  <div className="mb-4 p-3 bg-[#1E293B] rounded-lg border border-[#334155]">
                    <p className="text-xs text-[#94A3B8] mb-2">📝 条件判断示例：</p>
                    <div className="text-xs text-[#64748B] space-y-1">
                      <p>• 所有新主播：<code className="text-[#3B82F6]">[新主播任务流水]*0.3</code></p>
                      <p>• 指定主播特殊分成：<code className="text-[#3B82F6]">[主播昵称]==="小洞里"?[新主播任务流水]*0.8:[新主播任务流水]*0.3</code></p>
                      <p>• 2个主播7成，8个主播3成：<code className="text-[#3B82F6]">([主播昵称]==="张三"||[主播昵称]==="李四")?[新主播任务流水]*0.7:[新主播任务流水]*0.3</code></p>
                    </div>
                  </div>
                  {/* 选择经纪人 */}
                  <div className="mb-3">
                    <label className="text-xs text-[#94A3B8] mb-1 block flex items-center gap-1"><UserCheck className="w-3 h-3" />选择经纪人</label>
                    <select value={selectedBroker} onChange={e => setSelectedBroker(e.target.value)} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
                      <option value="">使用默认测试值</option>
                      {brokers.map(b => (<option key={b.name} value={b.name}>{b.name}</option>))}
                    </select>
                  </div>
                  {/* 选择月份 */}
                  {selectedBroker && brokerMonths.length > 0 && (
                    <div className="mb-3">
                      <label className="text-xs text-[#94A3B8] mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3" />选择月份</label>
                      <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
                        {brokerMonths.map(m => (<option key={m} value={m}>{m}</option>))}
                      </select>
                    </div>
                  )}
                  <div className="flex gap-2 mb-3">
                    <button onClick={handleTest} className="px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm">测试</button>
                  </div>
                  {testResult !== null && (
                    <div className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle className="w-4 h-4" />计算结果: {testResult.toFixed(2)}</div>
                  )}
                  {testError && (
                    <div className="flex items-center gap-2 text-red-400 text-sm"><AlertTriangle className="w-4 h-4" />{testError}</div>
                  )}
                  {testMeta && (
                    <div className="mt-2 text-xs text-[#64748B]">
                      数据来源: {testMeta.month} 月 | 共 {testMeta.count} 条主播记录汇总
                    </div>
                  )}
                  {Object.keys(testFieldValues).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[#334155] space-y-1">
                      <p className="text-xs text-[#64748B] mb-1">标签值：</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(testFieldValues).map(([field, value]) => (
                          <span key={field} className="text-xs bg-[#334155]/50 px-2 py-1 rounded text-[#94A3B8] font-mono">
                            [{DB_TO_LABEL[field] || field}]={typeof value === 'number' ? value.toFixed(0) : value}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 保存/取消 */}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowEdit(false)} className="flex-1 py-2.5 border border-[#334155] rounded-lg text-sm hover:bg-[#334555]/30">取消</button>
                  <button onClick={handleSave} className="flex-1 py-2.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm">保存</button>
                </div>
              </div>

              {/* ===== 右侧：字段选择器（自适应高度 + 拖拽） ===== */}
              <div className="bg-[#0F172A] rounded-lg border border-[#334155] p-4 flex flex-col" style={{ maxHeight: 'calc(90vh - 80px)' }}>
                {/* 标题 */}
                <div className="flex items-center justify-between mb-3 shrink-0">
                  <h4 className="text-sm font-medium">字段选择器</h4>
                  <span className="text-xs text-[#64748B]">{Object.keys(FIELD_LABELS).length}个</span>
                </div>
                {/* 分类列表 - flex-1 自适应 */}
                <div className="flex-1 min-h-0 overflow-y-auto space-y-1">
                  {fieldGroups.map((group, groupIdx) => (
                    <div
                      key={group.name}
                      className={`rounded transition-all ${
                        dragState.type === 'field' && dragState.targetGroupIdx === groupIdx
                          ? 'bg-[#3B82F6]/10 border border-[#3B82F6]/40'
                          : ''
                      } ${dragState.type === 'group' && dragState.groupIdx === groupIdx ? 'opacity-50' : ''}`}
                      onDragOver={(e) => handleGroupDragOver(groupIdx, e)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleGroupDrop(groupIdx, e)}
                    >
                      {/* 分类标题（可拖拽） */}
                      <div
                        draggable
                        onDragStart={(e) => handleGroupDragStart(groupIdx, e)}
                        onDragEnd={handleDragEnd}
                        className="flex items-center gap-1 w-full px-2 py-1.5 text-xs text-[#94A3B8] hover:text-white hover:bg-[#334155]/30 rounded transition-all cursor-grab active:cursor-grabbing select-none group/title"
                      >
                        <GripVertical className="w-3 h-3 text-[#475569] opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0" />
                        <button onClick={() => toggleGroup(group.name)} className="flex items-center gap-1 shrink-0">
                          {expandedGroups.has(group.name) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                        <span className="flex-1">{group.name}</span>
                        <span className="text-[#475569] text-[10px]">{group.fields.length}</span>
                      </div>
                      {/* 字段列表 */}
                      {expandedGroups.has(group.name) && (
                        <div className="ml-4 space-y-0.5 relative">
                          {/* 分类顶部放置区（字段拖入该分类） */}
                          <div
                            className={`h-1 rounded transition-all ${dragState.type === 'field' && dragState.targetGroupIdx === groupIdx && dragState.targetFieldIdx === -1 ? 'bg-[#3B82F6]' : 'bg-transparent'}`}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragState(prev => ({ ...prev, targetGroupIdx: groupIdx, targetFieldIdx: -1 })); }}
                            onDragLeave={(e) => { e.stopPropagation(); }}
                            onDrop={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              if (dragState.type === 'field' && dragState.groupIdx !== -1 && dragState.fieldIdx !== undefined) {
                                setFieldGroups(prev => {
                                  const next = prev.map(g => ({ ...g, fields: [...g.fields] }));
                                  const [removed] = next[dragState.groupIdx].fields.splice(dragState.fieldIdx, 1);
                                  next[groupIdx].fields.unshift(removed);
                                  return next.filter(g => g.name === '未分类' || g.fields.length > 0);
                                });
                              }
                              setDragState({ type: null, groupIdx: -1 });
                            }}
                          />
                          {group.fields.map((label, fieldIdx) => (
                            <div key={`${group.name}-${label}`} className="relative">
                              {/* 字段上方放置指示线 */}
                              <div
                                className={`absolute -top-0.5 left-0 right-0 h-0.5 rounded transition-all z-10 ${
                                  dragState.type === 'field' && dragState.targetGroupIdx === groupIdx && dragState.targetFieldIdx === fieldIdx
                                    ? 'bg-[#3B82F6]' : 'bg-transparent'
                                }`}
                                onDragOver={(e) => handleFieldDragOver(groupIdx, fieldIdx, e)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleFieldDrop(groupIdx, fieldIdx, e)}
                              />
                              {/* 字段按钮（可拖拽） */}
                              <button
                                draggable
                                onDragStart={(e) => handleFieldDragStart(groupIdx, fieldIdx, e)}
                                onDragEnd={handleDragEnd}
                                onClick={() => insertField(label)}
                                className={`flex items-center w-full text-left px-2 py-1 text-xs text-[#64748B] hover:text-[#3B82F6] hover:bg-[#334155]/20 rounded transition-all font-mono cursor-grab active:cursor-grabbing ${
                                  dragState.type === 'field' && dragState.groupIdx === groupIdx && dragState.fieldIdx === fieldIdx ? 'opacity-50' : ''
                                }`}
                              >
                                <GripVertical className="w-2.5 h-2.5 text-[#475569] opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0 mr-0.5" />
                                [{label}]
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* 底部提示 */}
                <div className="mt-2 pt-2 border-t border-[#334155]/50 shrink-0">
                  <p className="text-[10px] text-[#475569]">拖拽分类可排序，拖拽标签可移动到其它分类</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>{toast.message}</div>}
    </div>
  );
}
